import {
  EventIdPublic,
  EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";
import type {
  EventParticipantOwnerView,
  EventParticipantViewUnion,
} from "@shared/schemas/event-participant-views.schemas";
import { dal } from "@frontend/shared/data/frontend-dal";
import type { FrontendDAL } from "@frontend/shared/data/frontend-dal";
import { useGatedAction } from "../gated-action";
import { gateEnabled } from "../gate-enabled";
import type { EventView } from "./use-event-actions";

type ParticipantHooks = FrontendDAL["participants"];
type DirectoryHooks = FrontendDAL["directory"];

/**
 * Which participant to scope to — the caller's own (`{ self: true }`) or a specific
 * one by id — mirroring backend `forParticipant({ self: true })` / `forParticipant(id)`.
 * Self resolves the `SelfView`; by-id resolves the `OwnerView` (owner viewing a
 * participant) or `SelfView` (self-by-id); anyone else is forbidden by the backend.
 */
export type ForParticipantScope =
  | { readonly self: true }
  | EventParticipantIdPublic;

function isSelfScope(
  scope: ForParticipantScope,
): scope is { readonly self: true } {
  return typeof scope === "object" && "self" in scope;
}

/**
 * Narrows a resolved participant record to the owner tier. The `event:owner` role
 * is only ever stamped on the owner-viewing (`OwnerView`) record the backend returns
 * for a by-id lookup, so its presence is a sound discriminant (no type assertion
 * needed) — mirrors `isOwnerEventView` in `use-event-owner-actions`.
 */
export function isOwnerParticipantView(
  record: EventParticipantViewUnion | undefined,
): record is EventParticipantOwnerView {
  return record?.$roles.includes("event:owner") ?? false;
}

export interface EventParticipantActionsContext {
  readonly event: EventView | undefined;
  readonly isLoading: boolean;
  readonly error: Error | null;
}

export interface EventParticipantMutations {
  readonly updateParticipant: ReturnType<
    ParticipantHooks["useUpdateParticipant"]
  >;
  readonly updatePreferences: ReturnType<
    ParticipantHooks["useUpdatePreferences"]
  >;
  readonly uploadPhoto: ReturnType<ParticipantHooks["useUploadPhoto"]>;
  readonly removePhoto: ReturnType<ParticipantHooks["useRemovePhoto"]>;
  readonly optInRemember: ReturnType<ParticipantHooks["useOptInRemember"]>;
  readonly requestIntro: ReturnType<DirectoryHooks["useRequestIntro"]>;
}

export interface EventParticipantQueries {
  /** Refetch the resolved participant record. */
  readonly refetch: () => void;
  readonly useGetPreferences: ParticipantHooks["useGetPreferences"];
  readonly useGetSuggestions: DirectoryHooks["useGetSuggestions"];
  readonly useGetConnectionRequests: DirectoryHooks["useGetConnectionRequests"];
  readonly useGetConnectedProfiles: DirectoryHooks["useGetConnectedProfiles"];
}

export interface EventParticipantActions extends EventParticipantQueries {
  readonly isLoading: boolean;
  readonly error: Error | null;
  /**
   * The resolved participant record (the scope's subject); `undefined` until it
   * loads or if forbidden. It's the `SelfView`|`OwnerView` union — a by-id
   * (owner-viewing) consumer that needs owner-only fields narrows with
   * `isOwnerParticipantView`.
   */
  readonly participant: EventParticipantViewUnion | undefined;
  /**
   * Self-participant mutations, gated to the `{ self: true }` scope — `null` while
   * loading, when the record didn't resolve, or for the by-id scope (owner
   * mutations like remove/invite live on `useForOwner`).
   */
  readonly mutations: EventParticipantMutations | null;
}

/**
 * Event-participant actions — mirrors backend `forParticipant(scope)`. Fetches the
 * scoped participant record (self → `useGetMyParticipant`, by-id → `useGetByIdPublic`)
 * and surfaces it as `participant`, with `isLoading`/`error` reflecting that fetch.
 * Authorization is the participant role for self (`event.$roles` carries
 * `event:participant`, resolved once the event loads) or the successful by-id fetch
 * for owner-viewing (the backend 403s a non-owner lookup) — it is NOT the record
 * load, so the self `mutations` bundle stays usable while `participant` is still
 * loading. Self-scope adds the `mutations` bundle and self query siblings
 * (`useGetPreferences`, and the intro reads `useGetReasons` / `useGetConnectionRequests`
 * [backend `getPendingIntros`] / `useGetConnectedProfiles`); these are inert/gated
 * off for the by-id (owner-viewing) scope.
 */
export function useEventParticipantActions(
  context: EventParticipantActionsContext,
  scope: ForParticipantScope,
): EventParticipantActions {
  const isSelf = isSelfScope(scope);
  const eventPresent = context.event !== undefined;
  const eventIdPublic =
    context.event?.idPublic ?? EventIdPublic.unsafeCreate("");
  const participantId = isSelf
    ? EventParticipantIdPublic.unsafeCreate("")
    : scope;

  const selfQuery = dal().participants.useGetMyParticipant(eventIdPublic, {
    enabled: isSelf && eventPresent,
  });
  const byIdQuery = dal().participants.useGetByIdPublic(
    eventIdPublic,
    participantId,
    { enabled: !isSelf && eventPresent },
  );
  const recordQuery = isSelf ? selfQuery : byIdQuery;

  const {
    isLoading: _isLoading,
    error: _error,
    subject: _subject,
    bundle,
    ...queries
  } = useGatedAction({
    useDeps: () => ({
      updateParticipant: dal().participants.useUpdateParticipant(),
      updatePreferences: dal().participants.useUpdatePreferences(),
      uploadPhoto: dal().participants.useUploadPhoto(),
      removePhoto: dal().participants.useRemovePhoto(),
      optInRemember: dal().participants.useOptInRemember(),
      requestIntro: dal().directory.useRequestIntro(),
    }),
    isLoading: () => context.isLoading,
    error: () => context.error,
    authorize: () => {
      if (isSelf) {
        const event = context.event;
        return event !== undefined && event.$roles.includes("event:participant")
          ? event
          : null;
      }
      return byIdQuery.data ?? null;
    },
    bundle: (d) => ({
      updateParticipant: d.updateParticipant,
      updatePreferences: d.updatePreferences,
      uploadPhoto: d.uploadPhoto,
      removePhoto: d.removePhoto,
      optInRemember: d.optInRemember,
      requestIntro: d.requestIntro,
    }),
    extra: (_d, { isAuthorized }): EventParticipantQueries => {
      const selfEnabled = isSelf && isAuthorized;
      return {
        refetch: () => void recordQuery.refetch(),
        useGetPreferences: (input) =>
          dal().participants.useGetPreferences({
            ...input,
            enabled: gateEnabled(selfEnabled, input.enabled),
          }),
        useGetSuggestions: (options) =>
          dal().directory.useGetSuggestions({
            ...options,
            enabled: gateEnabled(selfEnabled, options.enabled),
          }),
        useGetConnectionRequests: (options) =>
          dal().directory.useGetConnectionRequests({
            ...options,
            enabled: gateEnabled(selfEnabled, options.enabled),
          }),
        useGetConnectedProfiles: (options) =>
          dal().directory.useGetConnectedProfiles({
            ...options,
            enabled: gateEnabled(selfEnabled, options.enabled),
          }),
      };
    },
  });

  return {
    isLoading: context.isLoading || recordQuery.isLoading,
    error:
      recordQuery.error instanceof Error ? recordQuery.error : context.error,
    participant: recordQuery.data ?? undefined,
    // Owner-viewing (by-id) exposes only the record; self mutations stay null there.
    mutations: isSelf ? bundle : null,
    ...queries,
  };
}
